import { useState, useEffect } from 'react'
import { Rocket, CheckCircle2, ExternalLink } from 'lucide-react'
import { Button } from '@lovable/components/ui/button'
import { Input } from '@lovable/components/ui/input'
import { toast } from 'sonner'
import { t } from 'i18next'

interface Props {
  boardImage?: string
  onPublish: (gamename: string) => void
  onGoBack: () => void
  taskCount: number
  goalCount: number
  gameId?: number
  gameCreated: boolean
}

export const Publish: React.FC<Props> = ({
  boardImage,
  onPublish,
  onGoBack,
  taskCount,
  goalCount,
  gameId,
  gameCreated,
}: Props) => {
  const [gameName, setGameName] = useState('') //t('engine.publish.default_game_name', 'Photosynthesis Adventure')
  const [published, setPublished] = useState(false)
  const [openButtonEnabled, setOpenButtonEnabled] = useState(false)

  const handlePublish = () => {
    onPublish(gameName)
    setPublished(true)
    toast.success(t('engine.publish.publishing', 'Publishing the game...'))
  }

  useEffect(() => {
    if (gameCreated) {
      setTimeout(() => {
        toast.success(t('engine.publish.published_toast', 'Game published succesfully!'))
        setOpenButtonEnabled(true)
      }, 3000)
    }
  }, [gameCreated])

  if (published) {
    return (
      <div className='container max-w-3xl mx-auto p-8 animate-fade-in'>
        {/* Success State */}
        <div className='text-center mb-8'>
          <h1 className='text-4xl font-bold text-foreground mb-3'>
            {gameCreated && openButtonEnabled
              ? t('engine.publish.published_title', 'Game Published! 🎉')
              : t('engine.publish.publishing', 'Publishing the game...')}
          </h1>
          {gameCreated && openButtonEnabled && (
            <p className='text-lg text-muted-foreground'>
              {t('engine.publish.published_sub_title', 'Your game is now ready!         ')}
            </p>
          )}
        </div>

        {/* Game Info Card */}
        <div className='bg-card border border-border rounded-2xl p-8 shadow-lg mb-6'>
          <div className='mb-6'>
            <h2 className='text-2xl font-bold text-foreground mb-2'>{gameName}</h2>
          </div>

          <div className='flex gap-3'>
            <Button
              className='flex-1 bg-gradient-to-r from-primary to-primary-glow hover:opacity-90'
              onClick={() => {
                window.location.href = '/game-editor/' + gameId
              }}
              disabled={!openButtonEnabled}
            >
              <ExternalLink className='w-4 h-4 mr-2' />
              {t('engine.publish.open_in_seppo', 'Open in Seppo Editor')}
            </Button>
          </div>
        </div>

        {/* Next Steps */}
        <div className='bg-gradient-to-br from-secondary/10 to-primary/10 rounded-xl p-6 border border-border/50'>
          <h3 className='text-lg font-semibold text-foreground mb-4'>
            {t('engine.publish.next.title', "What's Next?")}
          </h3>

          <div className='space-y-3'>
            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5'>
                <span className='text-xs font-bold text-primary'>1</span>
              </div>
              <p className='text-sm text-muted-foreground'>
                {t('engine.publish.next.step1', "Preview your game in Seppo's editor")}
              </p>
            </div>

            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center shrink-0 mt-0.5'>
                <span className='text-xs font-bold text-secondary'>2</span>
              </div>
              <p className='text-sm text-muted-foreground'>
                {t('engine.publish.next.step2', 'Configure additional features in the game and task settings')}
              </p>
            </div>

            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5'>
                <span className='text-xs font-bold text-primary'>3</span>
              </div>
              <p className='text-sm text-muted-foreground'>
                {t('engine.publish.next.step3', 'Make any final adjustments to task content')}
              </p>
            </div>

            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center shrink-0 mt-0.5'>
                <span className='text-xs font-bold text-secondary'>4</span>
              </div>
              <p className='text-sm text-muted-foreground'>{t('engine.publish.next.step4', 'Start your game')}</p>
            </div>

            <div className='flex items-start gap-3'>
              <div className='w-6 h-6 rounded-full bg-success/20 flex items-center justify-center shrink-0 mt-0.5'>
                <span className='text-xs font-bold text-success'>5</span>
              </div>
              <p className='text-sm text-muted-foreground'>
                {t('engine.publish.next.step5', 'Share with players and watch them learn!')}
              </p>
            </div>
          </div>
        </div>

        <div className='mt-8 text-center'>
          <Button variant='outline' disabled={!openButtonEnabled} onClick={() => (window.location.href = '/engine')}>
            {t('engine.publish.create_another', 'Create Another Game')}
          </Button>
        </div>
      </div>
    )
  }
  return (
    <div className='container max-w-3xl mx-auto p-8 animate-fade-in'>
      {/* Header */}
      <div className='mb-8 text-center'>
        <h1 className='text-4xl font-bold text-foreground mb-3'>
          {' '}
          {t('engine.publish.launch_title', 'Ready to Launch! 🚀')}
        </h1>
        <p className='text-lg text-muted-foreground'>
          {t(
            'engine.publish.launch_subtitle',
            '   Give the game a name and continue to publish it to your Seppo account.',
          )}
        </p>
      </div>

      {/* Game Summary */}
      <div className='relative bg-card border border-border rounded-2xl overflow-hidden shadow-lg mb-8'>
        {/* Background Image with Overlay */}
        <div className='absolute inset-0 z-0'>
          <img
            src={boardImage}
            alt='Game Board Background'
            className='w-full h-full object-cover opacity-20 scale-110'
          />
          <div className='absolute inset-0 bg-gradient-to-br from-background/98 via-card/97 to-background/98' />
        </div>

        {/* Content */}
        <div className='relative z-10 p-8'>
          <h2 className='text-2xl font-bold text-foreground mb-6'>
            {t('engine.publish.summary_title', 'Game Summary')}
          </h2>

          <div className='space-y-6'>
            {/* Game Name */}
            <div>
              <label className='block text-sm font-medium text-foreground mb-2'>
                {t('engine.publish.game_name_label', 'Game Name')}
              </label>
              <Input
                value={gameName}
                onChange={(e) => setGameName(e.target.value)}
                className='text-lg h-12'
                placeholder={t('engine.publish.name_input_placeholder', 'Enter a catchy name for your game...')}
              />
            </div>

            {/* Features Checklist */}
            <div className='bg-muted/30 rounded-xl p-6'>
              <h3 className='text-sm font-semibold text-foreground mb-4'>
                {t('engine.publish.launch', 'Your game includes:')}
              </h3>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {[
                  t('engine.publish.summary_tasks', '%{taskCount} diverse task types', { taskCount: taskCount }),
                  t('engine.publish.summary_board', 'Custom game board image'),
                  t('engine.publish.summary_goals', '%{goalCount} main learning goals', { goalCount: goalCount }),
                  t('engine.publish.summary_time', '~%{taskCount} min estimated play time', { taskCount: 2.5 * taskCount }),
                  t('engine.publish.summary_feedback', 'Instant feedback system'),
                  t('engine.publish.summary_tracking', 'Progress tracking'),
                ].map((feature, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <CheckCircle2 className='w-5 h-5 text-success shrink-0' />
                    <span className='text-sm text-foreground'>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Publish Actions */}
      <div className='flex justify-center'>
        <Button
          onClick={handlePublish}
          size='lg'
          className='bg-gradient-to-r from-primary to-primary-glow hover:opacity-90 transition-opacity shadow-lg'
        >
          <Rocket className='w-5 h-5 mr-2' />
          {t('engine.publish.publish_to_seppo', 'Publish to Seppo')}
        </Button>
      </div>
    </div>
  )
}
